import React, { useState, useEffect } from "react";
import { ConfigProvider } from "./contexts/ConfigContext";
import { useLanguage } from "./contexts/LanguageContext";
import Header from "./components/Header/Header";
import Sidebar from "./components/Sidebar/Sidebar";
import MainCanvas from "./components/MainCanvas/MainCanvas";
import ProgressBar from "./components/ProgressBar/ProgressBar";
import { fetchConfig } from "./services/api";
import { Config } from "./types";
import "./App.css";

const App: React.FC = () => {
  const [config, setConfig] = useState<Config | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [configId, setConfigId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(true);
  const [windowWidth, setWindowWidth] = useState<number>(window.innerWidth);

  const { updateAvailableLanguages } = useLanguage();

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const id =
      urlParams.get("configId") || window.location.pathname.split("/").pop();

    if (id && id !== "") {
      setConfigId(id);
    } else {
      setError("Config ID not provided in URL");
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!configId) return;

    const loadConfig = async (): Promise<void> => {
      try {
        if (!config) {
          const configData = await fetchConfig(configId);
          setConfig(configData);

          updateAvailableLanguages(configData.languages);
        }
      } catch (err) {
        setError("Failed to load configuration");
        console.error("Config loading error:", err);
      } finally {
        setLoading(false);
      }
    };

    loadConfig();
  }, [configId, updateAvailableLanguages]);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      if (window.innerWidth >= 1200) {
        setDrawerOpen(true);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  if (loading) {
    return (
      <div className="app-loading">
        <div className="loading-spinner"></div>
        <p>Loading configuration...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="app-error">
        <h2>Configuration Error</h2>
        <p>{error}</p>
        <button onClick={() => window.location.reload()}>Retry</button>
      </div>
    );
  }

  const isDesktop = windowWidth >= 1200;

  return (
    <ConfigProvider config={config}>
      <div className="app">
        <Header
          drawerOpen={drawerOpen}
          onToggleDrawer={() => setDrawerOpen(!drawerOpen)}
          showToggle={!isDesktop}
        />
        <div className="app-content">
          {isDesktop ? (
            // Desktop: боковая панель
            <>
              {drawerOpen && (
                <div className="sidebar-container">
                  <Sidebar />
                </div>
              )}
              <div
                className={`main-content ${drawerOpen ? "with-sidebar" : ""}`}
              >
                <ProgressBar />
                <MainCanvas />
              </div>
            </>
          ) : (
            // Mobile: drawer
            <>
              <div
                className={`drawer-overlay ${drawerOpen ? "open" : ""}`}
                onClick={() => setDrawerOpen(false)}
              />
              <div className={`drawer ${drawerOpen ? "open" : ""}`}>
                <Sidebar />
              </div>
              <div className="main-content">
                <ProgressBar />
                <MainCanvas />
              </div>
            </>
          )}
        </div>
      </div>
    </ConfigProvider>
  );
};

export default App;
